import { combineReducers } from 'redux'
import { Record, Map, Set, OrderedSet, List, fromJS } from 'immutable'
import * as actions from './userActions'
import * as config from '../../config/config'

export const CustomFeedRecord = Record({
  id: null,
  url: null,
  name: null,
  categoryId: null,
}, 'CustomFeedRecord')

function nightMode(state = false, action) {
  switch (action.type) {
    case actions.SET_NIGHT_MODE_SUCCESS:
      return !!action.active

    case actions.SET_TEMPORARY_PROFILE:
      return !!action.profileData.nightMode

    case actions.SET_ACTIVE_USER_PROFILE:
      // Night mode is currently only for temporary profile.
      // When using actual profile, we will respect the user selected theme.
      return false

    default:
      return state
  }
}

function frontPage(state = '/', action) {
  switch (action.type) {

    case actions.SET_ACTIVE_USER_PROFILE: {
      return action.profile.options['www-frontPage'] || state
    }

    case actions.LOGOUT_USER_SUCCESS:
      return '/'

    case actions.CHANGE_FRONT_PAGE_FAILURE:
      return state

    default:
      return state
  }
}

function theme(state = 'classic', action) {
  switch (action.type) {

    case actions.SET_ACTIVE_USER_PROFILE:
      return action.profile.options['www-theme'] || state

    case actions.LOGOUT_USER_SUCCESS:
      return 'classic'

    default:
      return state
  }
}

function layoutWidth(state = actions.LAYOUT_WIDTH_NORMAL, action) {
  switch (action.type) {

    case actions.SET_ACTIVE_USER_PROFILE:
      return action.profile.options['www-layoutWidth'] || state

    case actions.LOGOUT_USER_SUCCESS:
      return actions.LAYOUT_WIDTH_NORMAL

    default:
      return state
  }
}

function profiles(state = Map(), action) {
  switch (action.type) {
    case actions.FETCH_READYMADE_PROFILES_SUCCESS: {
      let profiles = Map()
      for (const profile of action.response.data) {
        profiles = profiles.set(profile.id, Map({
          id: profile.id,
          name: profile.name,
          categoryBlacklist: Set(profile.categoryBlacklist.map(c => c.id)),
        }))
      }
      return profiles
    }

    default:
      return state
  }
}

function activePresetProfile(state = null, action) {
  switch (action.type) {
    case actions.SET_ACTIVE_PRESET_PROFILE:
      return action.profileId

    case actions.SET_ACTIVE_USER_PROFILE:
    case actions.LOGOUT_USER_SUCCESS:
      return null

    default:
      return state
  }
}

function overlayIsOpen(state = false, action) {
  switch (action.type) {
    case actions.TOGGLE_OVERLAY_OPEN:
      return action.toggleState

    default:
      return state
  }
}

function showUserFeedsBoxInSidebar(state = true, action) {
  switch (action.type) {
    case actions.SET_ACTIVE_USER_PROFILE:
      return action.profile.options['www-showUserFeedsBoxInSidebar'] !== 'false'

    case actions.LOGOUT_USER_SUCCESS:
      return true

    default:
      return state
  }
}

const initialWlBlState = Map({
  whitelisted: Set(),
  blacklisted: Set(),
})

function categories(state = initialWlBlState, action) {
  switch (action.type) {
    case actions.SET_ACTIVE_USER_PROFILE:
      return state
        .set('whitelisted', Set(action.profile.categoryWhitelist.map(category => category.id)))
        .set('blacklisted', Set(action.profile.categoryBlacklist.map(category => category.id)))

    case actions.SET_TEMPORARY_PROFILE:
      return state
        .set('whitelisted', Set(action.profileData.whitelistedCats))
        .set('blacklisted', Set(action.profileData.blacklistedCats))

    case actions.LOGOUT_USER_SUCCESS:
      return initialWlBlState

    default:
      return state
  }
}

function tags(state = initialWlBlState, action) {
  switch (action.type) {
    case actions.SET_ACTIVE_USER_PROFILE:
      return state
        .set('whitelisted', Set(action.profile.tagWhitelist.map(tag => tag.id)))
        .set('blacklisted', Set(action.profile.tagBlacklist.map(tag => tag.id)))

    case actions.SET_TEMPORARY_PROFILE:
      return state
        .set('whitelisted', Set(action.profileData.whitelistedTags))
        .set('blacklisted', Set(action.profileData.blacklistedTags))

    case actions.LOGOUT_USER_SUCCESS:
      return initialWlBlState

    default:
      return state
  }
}

function blacklistedSources(state = Set(), action) {
  switch (action.type) {
    case actions.SET_ACTIVE_USER_PROFILE: {
      const ids = action.profile.sourceBlacklist.map(source => source.id)
      return state.intersect(ids).union(ids)
    }

    case actions.LOGOUT_USER_SUCCESS:
      return state.clear()

    default:
      return state
  }
}

function subscribedSources(state = Set(), action) {
  switch (action.type) {
    case actions.SET_ACTIVE_USER_PROFILE: {
      const ids = action.profile.sourceSubscriptions.map(source => source.id)
      return state.intersect(ids).union(ids)
    }

    case actions.LOGOUT_USER_SUCCESS:
      return state.clear()

    default:
      return state
  }
}

function recommendedCategories(state = Map(), action) {
  switch (action.type) {

    case actions.FETCH_RECOMMENDED_CATEGORIES_SUCCESS: {
      const category = action.data.map(content => content.category)
        .reduce((acc, cat) => {
          return cat
        }, Map())

      const items = action.data.map(content => content.items)
        .reduce((acc, item) => {
          return item.map(item => item.id)
        }, List())

      return state
        .set('timestamp', action.timestamp)
        .set('category', Map(category))
        .set('itemsIds', items)
    }
    default:
      return state
  }
}

function wordFilters(state = OrderedSet(), action) {
  switch (action.type) {
    case actions.SET_ACTIVE_USER_PROFILE: {
      const expressions = action.profile.itemWordFilters.map(filter => filter.text)
      const newSet = OrderedSet(expressions)
      return state.equals(newSet) ? state : newSet
    }

    case actions.LOGOUT_USER_SUCCESS:
      return state.clear()

    default:
      return state
  }
}

function savedSearches(state = OrderedSet(), action) {
  switch (action.type) {
    case actions.SET_ACTIVE_USER_PROFILE: {
      const terms = action.profile.savedSearches.map(search => search.searchTerm)
      const newSet = OrderedSet(terms)
      return state.equals(newSet) ? state : newSet
    }

    case actions.LOGOUT_USER_SUCCESS:
      return state.clear()

    default:
      return state
  }
}

function voteFilterThreshold(state = 0, action) {
  switch (action.type) {
    case actions.SET_ACTIVE_USER_PROFILE:
      return -Math.abs(action.profile.negativeVoteFilterThreshold)

    case actions.LOGOUT_USER_SUCCESS:
      return 0

    default:
      return state
  }
}

function customFeeds(state = OrderedSet(), action) {
  switch (action.type) {
    case actions.SET_ACTIVE_USER_PROFILE: {
      const feeds = action.profile.customFeeds.map(feed => CustomFeedRecord({
        id: feed.id,
        url: feed.url,
        name: feed.name,
        categoryId: feed.category?.id,
      }))
      const newSet = OrderedSet(feeds)
      return state.equals(newSet) ? state : newSet
    }

    case actions.LOGOUT_USER_SUCCESS:
      return state.clear()

    default:
      return state
  }
}

const initialUserInfo = fromJS({
  username: '',
  email: '',
  userId: null,
  userHash: '',
  almaIdHash: '',
  jwt: null,
})

function userInfo(state = initialUserInfo, action) {
  switch (action.type) {
    case actions.SET_LOGGED_IN_USER:
      return state
        .set('username', action.username)
        .set('email', action.email)
        .set('userId', action.userId)
        .set('userHash', action.userHash)
        .set('almaIdHash', action.almaIdHash)
        .set('jwt', action.jwt)
    case actions.CLEAR_USER_JWT:
      return state
        .set('jwt', null)
    case actions.LOGOUT_USER_SUCCESS:
      return initialUserInfo

    default:
      return state
  }
}

function defaultLocation(state = config.defaultLocation, action) {
  switch (action.type) {
    case actions.SET_ACTIVE_USER_PROFILE:
      return action.profile.weatherLocations.defaultLocation.id
    case actions.LOGOUT_USER_SUCCESS:
      return config.defaultLocation

    default:
      return state
  }
}

function locations(state = OrderedSet(), action) {
  switch (action.type) {
    case actions.SET_ACTIVE_USER_PROFILE:
      return fromJS(action.profile.weatherLocations.locations).toOrderedSet()
    case actions.LOGOUT_USER_SUCCESS:
      return OrderedSet()

    default:
      return state
  }
}

function timestamp(state = 0, action) {
  switch (action.type) {
    case actions.SET_ACTIVE_USER_PROFILE:
      return action.timestamp
    case actions.LOGOUT_USER_SUCCESS:
      return 0
    default:
      return state
  }
}


export default combineReducers({
  activePresetProfile,
  blacklistedSources,
  categories,
  customFeeds,
  defaultLocation,
  frontPage,
  layoutWidth,
  locations,
  nightMode,
  overlayIsOpen,
  profiles,
  recommendedCategories,
  savedSearches,
  showUserFeedsBoxInSidebar,
  subscribedSources,
  tags,
  theme,
  timestamp,
  userInfo,
  voteFilterThreshold,
  wordFilters,
})
